import { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore, useSites, useLogout, useTranslation } from '@bms/common';
import { PageSpinner } from '@/components/ui/spinner';
import { Button } from '@/components/ui/button';
import { LogOut } from 'lucide-react';
import MainLayout from './layouts/MainLayout';
import { ErrorBoundary } from './components/ErrorBoundary';
import { useHousekeepingAccess } from './lib/housekeepingMode';

// Lazy load pages
// LoginPage is lazy despite being on the first-visit path: it is shown at most
// once per session and nothing else in the app imports it. The service worker
// precaches every chunk, so after the first visit it loads from cache rather
// than over the network.
const LoginPage = lazy(() => import('./pages/LoginPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const RoomPage = lazy(() => import('./pages/RoomPage'));
const AccessControlPage = lazy(() => import('./pages/AccessControlPage'));
const ActivityPage = lazy(() => import('./pages/ActivityPage'));
const HousekeepingPage = lazy(() => import('./pages/HousekeepingPage'));
const VisitorsPage = lazy(() => import('./pages/VisitorsPage'));
const CardsPage = lazy(() => import('./pages/CardsPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const HotelSettingsPage = lazy(() => import('./pages/HotelSettingsPage'));

// Component to auto-select default site
function SiteInitializer({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const currentSiteCode = useAuthStore((state) => state.currentSiteCode);
  const setSite = useAuthStore((state) => state.setSite);
  const authLogout = useAuthStore((state) => state.logout);
  const logout = useLogout();
  const { data: sites, isLoading } = useSites();

  useEffect(() => {
    if (!currentSiteCode && sites && sites.length > 0) {
      const defaultSite = sites.find((s) => s.siteCode === 'default') || sites[0];
      setSite(defaultSite.siteCode);
    }
  }, [currentSiteCode, sites, setSite]);

  if (isLoading) {
    return <PageSpinner />;
  }

  if (!isLoading && (!sites || sites.length === 0)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen px-6 text-center">
        <h2 className="font-display text-title mb-2">{t('shell.noSites.title')}</h2>
        <p className="text-ui text-muted-foreground mb-6">
          {t('shell.noSites.body')}
        </p>
        <Button
          variant="destructive"
          onClick={() => {
            logout.mutate();
            authLogout();
            window.location.href = '/login';
          }}
        >
          <LogOut className="h-4 w-4 mr-2" />
          {t('nav.logout')}
        </Button>
      </div>
    );
  }

  return <>{children}</>;
}

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function RoleHomeRoute() {
  const { isAttendantMode, isSupervisorHome, isResolving } = useHousekeepingAccess();
  const { search } = useLocation();

  if (isResolving) return <PageSpinner />;
  if (isAttendantMode) return <Navigate to="/housekeeping" replace />;
  // The supervisor home (R3) lands on Housekeeping but keeps the Desk: a
  // deep link (`/?room=…` from search or a toast) still opens the board.
  if (isSupervisorHome && !search) return <Navigate to="/housekeeping" replace />;
  return <DashboardPage />;
}

function StandardUserRoute({ children }: { children: React.ReactNode }) {
  const { isAttendantMode, isResolving } = useHousekeepingAccess();

  if (isResolving) return <PageSpinner />;
  if (isAttendantMode) {
    return <Navigate to="/housekeeping" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <Suspense fallback={<PageSpinner />}>
      <Routes>
        <Route path="/login" element={<ErrorBoundary><LoginPage /></ErrorBoundary>} />
        <Route
          path="/*"
          element={
            // The shell (sidebar, room dialog, card taps) sits outside every
            // page boundary; without this one a shell error blanks the desk.
            <ErrorBoundary>
              <ProtectedRoute>
                <SiteInitializer>
                  <MainLayout />
                </SiteInitializer>
              </ProtectedRoute>
            </ErrorBoundary>
          }
        >
          <Route index element={<ErrorBoundary><RoleHomeRoute /></ErrorBoundary>} />
          <Route path="dashboard" element={<ErrorBoundary><StandardUserRoute><DashboardPage /></StandardUserRoute></ErrorBoundary>} />
          <Route path="rooms/:roomCode" element={<ErrorBoundary><StandardUserRoute><RoomPage /></StandardUserRoute></ErrorBoundary>} />
          <Route path="activity" element={<ErrorBoundary><StandardUserRoute><ActivityPage /></StandardUserRoute></ErrorBoundary>} />
          <Route path="access" element={<ErrorBoundary><StandardUserRoute><AccessControlPage /></StandardUserRoute></ErrorBoundary>} />
          <Route path="access-control" element={<ErrorBoundary><StandardUserRoute><AccessControlPage /></StandardUserRoute></ErrorBoundary>} />
          <Route path="housekeeping" element={<ErrorBoundary><HousekeepingPage /></ErrorBoundary>} />
          {/* Front office — the supervisor tier of BMS-spec §14.2. Every page
              here gates itself on permission codes; the route is open to any
              standard user because the page, not the router, is the honest
              place to say "you do not hold this". */}
          <Route path="visitors" element={<ErrorBoundary><StandardUserRoute><VisitorsPage /></StandardUserRoute></ErrorBoundary>} />
          <Route path="cards" element={<ErrorBoundary><StandardUserRoute><CardsPage /></StandardUserRoute></ErrorBoundary>} />
          <Route path="reports" element={<ErrorBoundary><StandardUserRoute><ReportsPage /></StandardUserRoute></ErrorBoundary>} />
          <Route path="hotel-settings" element={<ErrorBoundary><StandardUserRoute><HotelSettingsPage /></StandardUserRoute></ErrorBoundary>} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
